<script setup>
import {ref, onMounted} from 'vue'
import axios from 'axios'

const props = defineProps({
    initialDiscount: {
        type: String,
        default: '0.00'
    }
})

const emit = defineEmits(['notification', 'discountUpdated', 'updateCart'])

// Data properties
const pointsToUse = ref('')
const pointsLoading = ref(false)
const pointsMessage = ref('')
const pointsSuccess = ref(false)
const pointsApplied = ref(false)
const pointsDiscount = ref(props.initialDiscount)
const availablePoints = ref(0)
const conversionRate = ref(1) // Taux de conversion des points en euros

// Format price function
const formatPrice = (price) => {
    return parseInt(price)
}

// Fetch available points
const fetchAvailablePoints = async () => {
    try {
        // Dans une implémentation réelle, vous devriez appeler une API pour obtenir les points disponibles
        // Par exemple:
        const response = await axios.get('/api/checkout/cart/get-points')
        availablePoints.value = response.data.data.points
        if (response.data.data.usedPoints) {
            pointsToUse.value = response.data.data.usedPoints
            processPoints(response.data.data.discount)
        }


    } catch (error) {
        console.error('Erreur lors de la récupération des points disponibles:', error)
    }
}

// Apply points function
const applyPoints = async () => {
    if (!pointsToUse.value || pointsLoading.value || pointsToUse.value <= 0) return

    pointsLoading.value = true
    pointsMessage.value = ''

    try {
        // Appel API réel pour appliquer les points
        const response = await axios.post('/api/checkout/cart/use-points', {
            points: pointsToUse.value
        })

        if (!response.data.data.error) {
            pointsToUse.value = response.data.data.usedPoints
            availablePoints.value = response.data.data.points
            processPoints(response.data.data.discount)
            emit('updateCart', response.data.data.cart)
        } else {
            pointsSuccess.value = false
            pointsMessage.value = response.data.data.message || 'Erreur lors de l\'application des points.'
            pointsApplied.value = false

            // Émettre l'événement pour la notification
            emit('notification', pointsMessage.value, 'error')
        }
    } catch (error) {
        pointsSuccess.value = false
        pointsMessage.value = 'Une erreur est survenue. Veuillez réessayer.'
        console.error('Erreur lors de l\'application des points:', error)

        // Émettre l'événement pour la notification
        emit('notification', 'Erreur lors de l\'application des points.', 'error')
    } finally {
        pointsLoading.value = false
    }
}

const processPoints = (
    discount,
) => {
    pointsSuccess.value = true
    pointsMessage.value = 'Points appliqués avec succès!'
    pointsApplied.value = true

    // Calculer la valeur en euros des points utilisés
    pointsDiscount.value = discount


    // Émettre l'événement pour la notification
    emit('notification', 'Points appliqués avec succès!', 'success')

    // Émettre l'événement pour mettre à jour le montant de la réduction
    emit('discountUpdated', pointsDiscount.value)
}

// Remove points function
const removePoints = async () => {
    pointsLoading.value = true

    try {
        // Dans une implémentation réelle, vous devriez appeler une API pour supprimer les points
        // Par exemple:
        const response = await axios.post('/api/checkout/cart/cancel-points')

        // Remettre les points dans le solde disponible
        availablePoints.value = response.data.data.points

        pointsToUse.value = ''
        pointsMessage.value = 'Points supprimés.'
        pointsSuccess.value = true
        pointsApplied.value = false
        pointsDiscount.value = '0.00'

        emit('updateCart', response.data.data.cart)

        // Émettre l'événement pour mettre à jour le montant de la réduction
        emit('discountUpdated', pointsDiscount.value)

        // Émettre l'événement pour la notification
        emit('notification', 'Points supprimés.', 'success')
    } catch (error) {
        // Gérer l'erreur
        emit('notification', 'Erreur lors de la suppression des points.', 'error')
    } finally {
        pointsLoading.value = false
    }
}

// Fetch available points on component mount
onMounted(() => {
    fetchAvailablePoints()
})
</script>

<template>
    <div class="points-section">
        <h4 class="section-title">
            <i class="bi bi-coin me-2"></i>Utiliser mes points
        </h4>
        <p class="text-muted mb-3">Vous avez <strong>{{ availablePoints }}</strong> points disponibles. Utilisez-les
            pour bénéficier
            d'une réduction sur votre commande.</p>

        <div class="points-container">
            <div class="points-form" :class="{'is-loading': pointsLoading}">
                <div class="input-group">
          <span class="input-group-text">
            <i class="bi bi-wallet2"></i>
          </span>
                    <input
                        type="number"
                        class="form-control"
                        v-model="pointsToUse"
                        placeholder="Nombre de points à utiliser"
                        :disabled="pointsLoading || pointsApplied"
                        :max="availablePoints"
                        min="0"
                    >
                    <button
                        class="btn btn-apply-points"
                        type="button"
                        @click="applyPoints"
                        :disabled="!pointsToUse || pointsLoading || pointsToUse <= 0 || pointsToUse > availablePoints || pointsApplied"
                    >
                        <span v-if="!pointsLoading">Appliquer</span>
                        <span v-else class="spinner-border spinner-border-sm" role="status"
                              aria-hidden="true"></span>
                    </button>
                </div>
            </div>

            <div v-if="pointsMessage" class="points-message mt-2"
                 :class="{'is-success': pointsSuccess, 'is-error': !pointsSuccess}">
                <i :class="['bi', pointsSuccess ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill', 'me-2']"></i>
                {{ pointsMessage }}
            </div>

            <div v-if="pointsApplied" class="applied-points mt-3">
                <div class="applied-points-details">
                    <div class="points-badge">
                        <i class="bi bi-check-circle-fill me-1"></i>
                        {{ pointsToUse }} points utilisés
                    </div>
                    <div class="discount-amount">
                        -{{ formatPrice(pointsDiscount) }}€
                    </div>
                </div>
                <button class="btn-remove-points" @click="removePoints">
                    <i class="bi bi-x-lg"></i>
                </button>
            </div>
        </div>
    </div>
</template>
